/**
 * Pure helpers for resolving entity references safely when linked
 * employees/services may have been deleted without cascade cleanup.
 */

export function getServiceResourceIds(
  servicesById: Record<string, { resources?: string[] }> | null | undefined,
  serviceId: string
): string[] {
  const service = servicesById && servicesById[serviceId];
  if (!service || !Array.isArray(service.resources)) {
    return [];
  }
  return service.resources;
}

export function resolveResourcesById<T extends { _id: string }>(
  resourceIds: string[] | null | undefined,
  resourcesById: Record<string, T>
): T[] {
  return (resourceIds || [])
    .map(id => resourcesById[id])
    .filter((resource): resource is T => Boolean(resource));
}

export function filterOnlineBookableResources<
  T extends {
    availableForOnlineBookings?: boolean;
  }
>(resources: Array<T | undefined | null>): T[] {
  return resources.filter((resource): resource is T =>
    Boolean(resource && resource.availableForOnlineBookings)
  );
}

export function sortShopServices<
  T extends { name?: string; availableForOnlineBookings?: boolean }
>(servicesList: T[] | null | undefined): T[] {
  return (servicesList || [])
    .filter(
      service =>
        service &&
        typeof service.name === "string" &&
        service.availableForOnlineBookings
    )
    .sort((a, b) => a.name!.localeCompare(b.name!));
}

export function mergeBookingsAvailability(
  availabilityList: Array<Record<string, string[]>> | null | undefined
): Record<string, string[]> {
  if (!availabilityList || !availabilityList.length) {
    return {};
  }

  return availabilityList.reduce((merged, next) => {
    for (const date in next) {
      if (date in merged) {
        merged[date] = Array.from(
          new Set([...next[date], ...merged[date]])
        ).sort();
      } else {
        merged[date] = next[date];
      }
    }
    return merged;
  }, {} as Record<string, string[]>);
}

export function sortAvailabilityByDate(
  availability: Record<string, string[]> | null | undefined
): Record<string, string[]> {
  const sorted: Record<string, string[]> = {};
  Object.keys(availability || {})
    .sort()
    .forEach(key => {
      sorted[key] = availability![key];
    });
  return sorted;
}
